import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Provider } from '../../../types/api.types';
import ProviderCard from '../../../components/ProviderCard';
import ProviderCardSkeleton from '../../../components/ProviderCardSkeleton';
import Button from '../../../components/common/Button';

interface FeaturedProvidersSectionProps {
  providers: Provider[];
  loading: boolean;
  onProviderClick: (provider: Provider) => void;
  onProviderBooking?: (provider: Provider) => void;
  city?: string | null;
}

const TopRatedProvidersSection: React.FC<FeaturedProvidersSectionProps> = ({
  providers,
  loading,
  onProviderClick,
  onProviderBooking,
  city,
}) => {
  const navigate = useNavigate();

  const handleViewAll = () => {
    // Navigate with city filter if available
    if (city) {
      navigate(`/providers?topRatedCity=${encodeURIComponent(city)}`);
    } else {
      navigate('/providers');
    }
  };

  return (
    <section className="py-10 sm:py-16 px-3 xs:px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6 sm:mb-8">
          <div>
            <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
              Top Rated Providers{city ? ` in ${city}` : ''}
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 flex items-center gap-1">
              {city ? (
                <>
                  <MapPinIcon className="w-4 h-4 text-accent-600" />
                  <span>Top-rated professionals in your area</span>
                </>
              ) : (
                'Meet our most trusted and experienced professionals'
              )}
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={handleViewAll}
            className="flex items-center space-x-1 text-xs sm:text-sm px-2.5 py-1 sm:px-3 sm:py-1.5 h-auto self-start sm:self-auto"
          >
            <span>View All</span>
            <ArrowRightIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 xs:gap-4 sm:gap-6">
            <ProviderCardSkeleton count={3} />
          </div>
        ) : providers.length > 0 ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 xs:gap-4 sm:gap-6">
            {providers.map((provider, index) => (
              <ProviderCard
                key={provider._id}
                provider={provider}
                onClick={() => onProviderClick(provider)}
                onBookClick={onProviderBooking}
                scaleOnHover
                eagerLoad={index < 3}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <MapPinIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No providers found {city ? `in ${city}` : 'in your area'}
            </h3>
            <p className="text-gray-600 mb-4">
              We're expanding to more locations soon. Check out all our providers below.
            </p>
            <Button variant="primary" onClick={handleViewAll}>
              Browse All Providers
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TopRatedProvidersSection;
